// Draft-mapping helpers.
//
// The local plannerDraft store (lib/plannerDraft.js) uses field names
// convenient for the wizard UI.  Migration 0005's fn_validate_planner_draft
// enforces a stricter, versioned server shape.  These functions map
// between the two without inventing values or normalising enums by
// guessing.  Enum strings must already match server enums; the wizard
// forms produce values in the enum vocabulary so no renames are
// applied here.

const SERVER_KEYS = [
  'draft_schema_version',
  'days',
  'meals_per_day',
  'health_goal',
  'sex',
  'date_of_birth',
  'height_cm',
  'weight_kg',
  'activity_level',
  'calorie_source',
  'calorie_target',
  'macro_preset',
  'protein_target_g',
  'carbs_target_g',
  'fat_target_g',
  'allergies',
  'restrictions',
  'preferences',
  'wizard_step',
];

function intOrNull(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n) : null;
}

function numOrNull(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function stringOrNull(v) {
  return typeof v === 'string' && v.length > 0 ? v : null;
}

// Build allergies list in server object-shape.  UI stores an array of
// enum ids plus one optional other_description string.  We only emit
// entries whose enum id is in the local array (no invention).
function buildAllergiesList(local) {
  const ids = Array.isArray(local.allergies) ? local.allergies : [];
  const desc = (local.allergies_other_description || '').trim();
  return ids.map((id) => {
    const entry = { allergy: id };
    if (id === 'other' && desc) entry.other_description = desc;
    return entry;
  });
}

function buildRestrictionsList(local) {
  const ids = Array.isArray(local.restrictions) ? local.restrictions : [];
  const desc = (local.restrictions_other_description || '').trim();
  return ids.map((id) => {
    const entry = { restriction: id };
    if (id === 'other' && desc) entry.other_description = desc;
    return entry;
  });
}

function buildPreferencesList(local) {
  const ids = Array.isArray(local.preferences) ? local.preferences : [];
  return ids.slice();
}

// True if the local draft has at least one meaningful field set (used
// to avoid saving an empty default draft on first mount).
export function hasAnyWizardInput(local) {
  if (!local) return false;
  if (local.days || local.meals_per_day || local.health_goal) return true;
  if (local.calorie_source || local.calorie_target) return true;
  if (local.macro_preset) return true;
  const b = local.body || {};
  if (b.age || b.sex || b.activity_level) return true;
  if (b.normalized_height_cm || b.normalized_weight_kg) return true;
  if ((local.allergies || []).length) return true;
  if ((local.restrictions || []).length) return true;
  if ((local.preferences || []).length) return true;
  return false;
}

// Map local plannerDraft → the JSON body accepted by
// fn_validate_planner_draft / upsert_anonymous_draft /
// upsert_authenticated_planner_draft.
//
// Only includes fields that have been set.  Never emits keys outside
// SERVER_KEYS.  draft_schema_version is always 1 and always present.
export function toServerDraft(local) {
  const b = (local && local.body) || {};
  const out = { draft_schema_version: 1 };

  const days = intOrNull(local?.days);
  if (days !== null && days >= 1 && days <= 5) out.days = days;

  const mpd = intOrNull(local?.meals_per_day);
  if (mpd !== null && mpd >= 1 && mpd <= 5) out.meals_per_day = mpd;

  const hg = stringOrNull(local?.health_goal);
  if (hg) out.health_goal = hg;

  const sex = stringOrNull(b.sex);
  if (sex) out.sex = sex;

  const h = numOrNull(b.normalized_height_cm);
  if (h !== null && h >= 100 && h <= 250) out.height_cm = h;

  const w = numOrNull(b.normalized_weight_kg);
  if (w !== null && w >= 30 && w <= 300) out.weight_kg = w;

  const act = stringOrNull(b.activity_level);
  if (act) out.activity_level = act;

  const cs = stringOrNull(local?.calorie_source);
  if (cs) out.calorie_source = cs;

  const ct = intOrNull(local?.calorie_target);
  if (ct !== null && ct >= 800 && ct <= 6000) out.calorie_target = ct;

  const mp = stringOrNull(local?.macro_preset);
  if (mp) out.macro_preset = mp;

  const pg = intOrNull(local?.protein_g);
  if (pg !== null && pg >= 0 && pg <= 600) out.protein_target_g = pg;

  const cg = intOrNull(local?.carbs_g);
  if (cg !== null && cg >= 0 && cg <= 800) out.carbs_target_g = cg;

  const fg = intOrNull(local?.fat_g);
  if (fg !== null && fg >= 0 && fg <= 400) out.fat_target_g = fg;

  const alist = buildAllergiesList(local || {});
  if (alist.length) out.allergies = alist;

  const rlist = buildRestrictionsList(local || {});
  if (rlist.length) out.restrictions = rlist;

  const plist = buildPreferencesList(local || {});
  if (plist.length) out.preferences = plist;

  const step = intOrNull(local?.step_reached);
  if (step !== null && step >= 1 && step <= 12) out.wizard_step = step;

  return out;
}

// True when the local draft has enough fields to satisfy the required
// parameters of complete_customer_onboarding.  first_name, last_name
// and date_of_birth are NOT collected by the current wizard and are
// intentionally passed as null.
export function isOnboardingEligible(local) {
  const b = (local && local.body) || {};
  const hg = stringOrNull(local?.health_goal);
  const sex = stringOrNull(b.sex);
  const act = stringOrNull(b.activity_level);
  const cs = stringOrNull(local?.calorie_source);
  const mp = stringOrNull(local?.macro_preset);
  const h = numOrNull(b.normalized_height_cm);
  const w = numOrNull(b.normalized_weight_kg);
  const ct = intOrNull(local?.calorie_target);
  const alist = buildAllergiesList(local || {});
  return Boolean(
    hg && sex && act && cs && mp &&
      h !== null && h >= 100 && h <= 250 &&
      w !== null && w >= 30 && w <= 300 &&
      ct !== null && ct >= 800 && ct <= 6000 &&
      alist.length > 0
  );
}

// Build the exact argument bag for complete_customer_onboarding.
// Returns { ok: true, args } when all values map cleanly to approved
// enum values / ranges; otherwise { ok: false, missing: [...] } so the
// caller can stop rather than fabricate a mapping.
//
// first_name / last_name / date_of_birth are NOT collected by the
// current wizard.  We pass null for each — customers.first_name /
// last_name / date_of_birth are nullable in migration 0001.
export function buildOnboardingArgs(local) {
  const missing = [];
  const b = (local && local.body) || {};

  const args = {
    p_onboarding_request_id: null, // set by caller (idempotency key)
    p_first_name: null,
    p_last_name: null,
    p_date_of_birth: null,
    p_sex: stringOrNull(b.sex),
    p_height_cm: numOrNull(b.normalized_height_cm),
    p_weight_kg: numOrNull(b.normalized_weight_kg),
    p_activity_level: stringOrNull(b.activity_level),
    p_health_goal: stringOrNull(local?.health_goal),
    p_calorie_source: stringOrNull(local?.calorie_source),
    p_calorie_target: intOrNull(local?.calorie_target),
    p_macro_preset: stringOrNull(local?.macro_preset),
    p_protein_target_g: intOrNull(local?.protein_g),
    p_carbs_target_g: intOrNull(local?.carbs_g),
    p_fat_target_g: intOrNull(local?.fat_g),
    p_allergies: buildAllergiesList(local || {}),
    p_restrictions: buildRestrictionsList(local || {}),
    p_preferences: buildPreferencesList(local || {}),
  };

  if (!args.p_sex) missing.push('sex');
  if (!args.p_activity_level) missing.push('activity_level');
  if (!args.p_health_goal) missing.push('health_goal');
  if (!args.p_calorie_source) missing.push('calorie_source');
  if (!args.p_macro_preset) missing.push('macro_preset');
  if (args.p_height_cm === null) missing.push('height_cm');
  if (args.p_weight_kg === null) missing.push('weight_kg');
  if (args.p_calorie_target === null) missing.push('calorie_target');
  if (!args.p_allergies.length) missing.push('allergies');

  if (missing.length) return { ok: false, missing };
  return { ok: true, args };
}

// Stable hash of the server draft body used to skip redundant saves.
export function draftSignature(serverDraft) {
  try {
    return JSON.stringify(serverDraft);
  } catch {
    return null;
  }
}

export const SERVER_DRAFT_KEYS = SERVER_KEYS;
